import { useEffect } from "react";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  FetchingStatus,
  useAppDispatch,
  useAppSelector,
} from "@store/store-helper";
import {
  fetchCurrentUser,
  fetchCurrentUserRoles,
} from "@store/user/user-thunks";
import { userStateSelector } from "@store/user/user-slice";

interface ReturnProps {
  /** The currently logged in user, or null if not fetched yet */
  currentUser: FaroCoreApiTypes.LoggedInUser | null;

  /** Roles of the currently logged in user */
  currentUserRoles: FaroCoreApiTypes.AdmUserRole[];

  /** Fetching status of the logged in user */
  fetchingCurrentUser: FetchingStatus;

  /** Fetching status of the logged in user's roles */
  fetchingCurrentUserRoles: FetchingStatus;
}

/**
 * Flag whether the user and user roles have been fetched.
 * Makes sure we only fetch the logged in user and its roles once during the app lifecycle.
 */
let hasLoggedUserBeenFetched = false;

/**
 * Hook returning the currently logged in user
 *
 * Returns an object of the fetched user and a loading flag, which is true while the user is being fetched
 */
export function useLoggedInUser(): ReturnProps {
  const dispatch = useAppDispatch();
  const { currentUser, currentUserRoles, fetchingUser } =
    useAppSelector(userStateSelector);

  // Fetch the currently logged in user and its roles
  useEffect(() => {
    // Do not fetch the logged in user or its roles again
    if (hasLoggedUserBeenFetched) {
      return;
    }
    hasLoggedUserBeenFetched = true;

    void Promise.all([
      dispatch(fetchCurrentUser()),
      dispatch(fetchCurrentUserRoles()),
    ]);
  }, [dispatch]);

  return {
    currentUser,
    currentUserRoles,
    fetchingCurrentUser: fetchingUser.currentUser,
    fetchingCurrentUserRoles: fetchingUser.currentUserRoles,
  };
}
